import React, { useState, useRef } from 'react';
import { SubmitButton } from 'Components/Fields/Buttons';
import './Dropzone.css';

const Dropzone = ({
  onSubmit,
  onAddFile,
  onRemoveFile,
  onClearFiles,
  submitting = false,
  children = null,
  multiple = false,
  maxFiles = 10,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const inputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files);
      e.currentTarget.value = null;
    }
  };

  const handleFile = (fileList) => {
    if (files.length <= maxFiles) {
      const newFiles = files;
      for (let i = 0; i < fileList.length; i++) {
        if (newFiles.length < maxFiles) {
          newFiles.push(fileList[i]);
          onAddFile(fileList[i]);
        } else {
          break;
        }
      }
      setFiles([...newFiles]);
      if (newFiles?.length === 1 && window?.scrollTo) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleRemoveFile = (index) => {
    let removeInput = false;
    const newFiles = files.filter((file, i) => {
      if (index === i && inputRef.current.value.includes(file?.[0]?.name)) {
        removeInput = true;
        onRemoveFile(file);
      }
      return index !== i;
    });
    if (removeInput) inputRef.current.value = null;
    setFiles([...newFiles]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(files);
  };

  const clearFiles = () => {
    inputRef.current.value = null;
    onClearFiles(files);
    setFiles([]);
    if (window && window.scrollTo) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="dropZone" onDragEnter={handleDrag}>
      <input
        ref={inputRef}
        multiple={multiple}
        type="file"
        id="dropZoneUpload"
        className="dropZoneUpload"
        onChange={handleChange}
        accept=".jpg,.jpeg,.png,.pdf,.gif,.bmp"
        data-testid="dropzoneInput"
      />
      {files.length === 0 && children}
      {files.length < maxFiles && (
        <label id="dropZoneLabel" htmlFor="dropZoneUpload">
          <div className="dropZoneLabelInfo">
            <img
              className="dropZoneUploadIcon"
              width="30"
              height="30"
              alt="upload-file"
              src="/account/static/images/drag-drop.svg"
            />
            <div className="dropZoneLabelText">
              <span>Drag & drop your files here </span>
              <span className="dropZoneLabelInfoBrowse">Browse</span>
            </div>
            <div className="dropZoneLabelDisclaimer">Only .jpg .jpeg .png .pdf .gif or .bmp</div>
          </div>
        </label>
      )}
      {files.length > 0 && (
        <div className="dropZoneFileList">
          {files.map((file, index) => {
            const fileName = file?.name;
            if (!file || !fileName) return null;
            return (
              <div
                className={`dropZoneFile ${index === files.length - 1 ? 'noBorder' : ''}`}
                key={`${index}-${fileName}`}
                data-heap-redact-text
              >
                <img
                  className="dropZonePreview"
                  src={URL.createObjectURL(file)}
                  alt={fileName}
                  width="34"
                  height="34"
                  data-heap-redact-text
                />
                <div className="dropZoneFileName" data-heap-redact-text>
                  {fileName}
                </div>
                <button className="dropZoneRemoveButton" onClick={() => handleRemoveFile(index)}>
                  <img
                    className="dropZoneRemoveIcon"
                    src="/account/static/images/delete.svg"
                    width="20"
                    height="20"
                    alt="delete-file"
                  />
                </button>
              </div>
            );
          })}
          <div className="dropZoneButtonContainer">
            <SubmitButton submitting={submitting} text="Continue" onClick={handleSubmit} />
            <button className="dropZoneButtonCancel" onClick={clearFiles}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {dragActive && files.length < maxFiles && (
        <div
          className="dropZoneElement"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        ></div>
      )}
    </div>
  );
};

export default Dropzone;
